import { Component, Listen, Prop } from "@stencil/core";
import { SwpChatbotWindowSettings, WebchatOptions, WebChatReadyEvent } from "./webchat-options.model";
import { DefaultEventHandlerConfigs } from "./webchat-event-handlers";
import { ContentStackConfigService } from "../../services/content-stack-configuration-service";
import { Environments, IConfigurationService } from "../../services/configuration-service.model"
import { IsValidStringValue } from "./property-helper";
import { ChatSessionService, ChatWindowState, HeaderState, InputState } from "../../services/chat-session.service";
import { AnalyticsService } from "../../services/analytics-service";
import { forwardAccessTokenFromUserMenu } from "./event-handlers/get-access-token-handler";
import { withGlobalFunctions } from "./decorators/with-global-functions";

@Component({
  tag: 'swp-chatbot-window',
  styleUrl: 'swp-chatbot-window.scss',
  shadow: false,
})
export class SwpChatbotWindow {
  /**
   * Cognigy endpoint url. Mandatory if property chatbotEndpointName is unspecified. Takes precedence over chatbotEndpointName + environment.
   */
  @Prop({ mutable: true, reflect: true }) cognigyConfigUrl: string;

  /**
   * The chatbot endpoint name refers to the Cognigy endpoints configuration in ContentStack you want to use. This points to a Cognigy endpoint url.
   * Mandatory if property cognigyConfigUrl is unspecified. Takes precedence over the data-chatbot-endpoint-name attribute on the script tag.
   *
   * You can also specify this value in the data-chatbot-endpoint-name attribute that loads this script tag.
   * E.g. `<script src=".." data-chatbot-endpoint-name="daan-prod">`.
   */
  @Prop({ mutable: true, reflect: true }) chatbotEndpointName: string;

  /**
   * Specifies what ContentStack environment the configuration will load.
   * Defaults to 'prod'. Only use for testing and development purposes!
   */
  @Prop({ mutable: true, reflect: true }) environment: Environments = 'prod';

  /**
   * Options object to pass along to Cognigy WebChat.
   * See https://github.com/Cognigy/WebchatWidget/blob/v3/docs/embedding.md#client-side-configuration
   */
  @Prop({ mutable: true }) options: WebchatOptions = { eventHandlerConfigs: [], deviceMinWidthToFocusInput: 880 };

  /**
   * Start a specific feature flow. Do not specify if you want the main flow to start.
   */
  @Prop({ mutable: true, reflect: true }) featureFlow: string | undefined;

  /**
   * Hides that chat bubble if set to true.
   */
  @Prop({ mutable: true, reflect: true }) hideBubbleWhenClosed: boolean = false;

  private webchat: any;

  private configurationService: IConfigurationService = ContentStackConfigService;
  private chatSessionService: ChatSessionService;
  private analyticsService: AnalyticsService;

  @Listen('postnl:webchat:settings:update', { target: 'window' })
  async updateSettings(event: CustomEvent<SwpChatbotWindowSettings>): Promise<void> {
    this.cognigyConfigUrl = event.detail.cognigyConfigUrl;
    this.chatbotEndpointName = event.detail.chatbotEndpointName;
    this.environment = IsValidStringValue(event.detail.environment) ? event.detail.environment : "prod";
    this.options = Object.assign(this.options ?? {}, event.detail.options);
    this.featureFlow = event.detail.featureFlow;
    this.hideBubbleWhenClosed = event.detail.hideBubbleWhenClosed;

    this.webchat.close();
    await this.reinitialize();
  }

  @Listen('postnl:webchat:open', { target: 'window' })
  async open(event: CustomEvent<{ analyticsName: any, featureFlow: string }>) {
    if (this.chatSessionService.getWindowState() === ChatWindowState.Open) return;

    let analyticsName = event.detail?.analyticsName || 'Start de chat via javascript';
    const featureFlow = event.detail?.featureFlow;

    if (!this.chatSessionService.popIsNewSession()) {
      analyticsName = `Verder met gesprek (${analyticsName})`;
    }
    this.analyticsService.dispatchFrontEndAnalytics(analyticsName);

    if (featureFlow) { // Starting feature flow based on open event
      this.setFeatureFlow(featureFlow);

      // Reload webchat since options are stateless and we want to updatee options to start feature flow based on "open" event
      await this.loadWebchat();
    }

    this.webchat.open();
    this.webchat.startConversation();

    this.changeBubbleVisibility(true);
  }

  @Listen('postnl:webchat:close', { target: 'window' })
  async close() {
    this.webchat.close();
  }

  componentDidLoad() {
    const webchatScript = document.createElement('script');
    webchatScript.src = 'https://github.com/Cognigy/Webchat/releases/download/v3.16.0/webchat.js';
    webchatScript.async = true;
    webchatScript.onload = this.onWebchatScriptLoad.bind(this);

    document.head.appendChild(webchatScript);
  }

  private async reinitialize(): Promise<void> {
    this.cleanUpWebchat();
    this.chatSessionService.reset();
    // Load it again
    return this.onWebchatScriptLoad();
  }

  private cleanUpWebchat() {
    // Remove any divs that contain the chat bubble
    document
      .querySelectorAll('div[data-cognigy-webchat-root]')
      .forEach(div => div.parentElement.remove());
  }

  private async onWebchatScriptLoad(): Promise<void> {
    if (typeof window.initWebchat !== 'function') {
      console.error('Cognigy WebchatWidget scripts loaded but initWebchat() is not a function');
      return;
    }

    this.updateOptions();
    return this.loadWebchat();
  }

  private updateOptions() {
    this.setOptionDefaults();
    this.setFeatureFlow();
  }

  private setOptionDefaults() {
    this.options.settings ??= {};
    this.options.settings.widgetSettings ??= {};
    this.options.settings.widgetSettings.disableInputAutofocus ??= true; // Disable autofocus input field for mobile users

    // The default local storage seems to be an infinitly growing collection...
    // Use session storage instead. We don't support continueing conversations after browser close anyway.
    this.options.settings.embeddingConfiguration ??= {};
    this.options.settings.embeddingConfiguration.useSessionStorage = true;
  }

  private setFeatureFlow(featureFlow?: string) {
    this.options.settings.startBehavior = {};

    const urlParams = new URLSearchParams(window.location.search);
    const featureFlowValue = featureFlow ?? urlParams.get('featureFlow') ?? this.featureFlow;

    if (IsValidStringValue(featureFlowValue)) {
      this.options.settings.startBehavior.startBehavior = "injection";
      this.options.settings.startBehavior.getStartedText = "";
      this.options.settings.startBehavior.getStartedData = { featureFlow: featureFlowValue }
    }
  }

  private async loadWebchat(): Promise<void> {
    const endpoint = IsValidStringValue(this.cognigyConfigUrl)
      ? this.cognigyConfigUrl
      : await this.getChatbotEndpointUrlFromConfiguration();

    if (!IsValidStringValue(endpoint)) {
      console.error('Either chatbot-endpoint-name or cognigy-config-url is required');
      return;
    }

    const key = encodeURI(endpoint);
    this.chatSessionService = new ChatSessionService(key);
    this.chatSessionService.updateHeaderState();

    const sessionId = await this.chatSessionService.restoreSessionFromApp() ||
      this.chatSessionService.getSessionId() ||
      this.chatSessionService.createNewSessionId();

    this.analyticsService = new AnalyticsService(() => sessionId);

    const options = {
      ...this.options,
      sessionId
    };

    const webchat = await window.initWebchat(endpoint, options);
    this.onWebchatLoaded(webchat);
  }

  private async getChatbotEndpointUrlFromConfiguration() {
    const chatbotEndpointName = this.chatbotEndpointName ?? document.querySelector('script[data-chatbot-endpoint-name]')?.getAttribute('data-chatbot-endpoint-name');
    if (!chatbotEndpointName) {
      return undefined;
    }

    const config = await this.configurationService.GetEndpointConfig(this.environment, { chatbotEndpointName });
    return config.endpointUrl;
  }

  private onWebchatLoaded(webchat: any): void {
    this.webchat = webchat;

    this.onWebchatRootAdded(() => {
      const showBubble = this.chatSessionService.getWindowState() === ChatWindowState.Closed ? !this.hideBubbleWhenClosed : true;
      this.changeBubbleVisibility(showBubble);
      this.changeHeaderVisibility();
      this.addToggleBehavior();
    });

    this.registerDefaultEventHandlers();
    webchat.registerAnalyticsService(event => this.handleEvent(event));

    const state = this.chatSessionService.getWindowState();
    if (state === ChatWindowState.Open) {
      webchat.open();
      webchat.startConversation();
    }

    this.afterLoad(webchat);
  }

  private changeHeaderVisibility(): void {
    const headerState = this.chatSessionService.getHeaderState();
    const chatbotHeader = document.getElementsByClassName('webchat-header-bar')?.[0] as HTMLElement;

    if (chatbotHeader) {
      chatbotHeader.style.display = headerState === HeaderState.Hidden ? 'none': 'flex';
    }
  }

  private addToggleBehavior() {
    const bubble = document.querySelector('button[data-cognigy-webchat-toggle]');
    bubble.addEventListener('click', () => {
      if (this.chatSessionService.getWindowState() === ChatWindowState.Open) {
        // When clicking on the bubble while window is open, it acts as minimize
        this.chatSessionService.setWindowState(ChatWindowState.Minimized);
        this.analyticsService.dispatchFrontEndAnalytics('Gesprek verbergen (bubble)');
      } else if (this.chatSessionService.popIsNewSession()) {
        // Starting a new session via the bubble
        this.analyticsService.dispatchFrontEndAnalytics('Start de chat (bubble)');
      } else {
        // Continue existing session via the bubble
        this.analyticsService.dispatchFrontEndAnalytics('Verder met gesprek (bubble)');
      }
    });
  }

  private registerDefaultEventHandlers() {
    if (this.options.eventHandlerConfigs == null) {
      this.options.eventHandlerConfigs = [];
    }
    for (const config of DefaultEventHandlerConfigs) {
      if (this.options.eventHandlerConfigs.find(_config => _config.eventId === config.eventId)) {
        continue;
      }
      this.options.eventHandlerConfigs.push(config);
    }
  }

  private handleEvent(event: any) {
    if (this.isCustomEvent(event)) {
      this.handleCustomEvent(event);
    }
    else if (event.type === 'webchat/open') { // webchat/open does not actually fire when it has opened but when it's about to
      this.onWebchatOpen(this.handleOpenEvent.bind(this));
      this.changeHeaderVisibility();
      this.forwardAccessTokenIfLoginWasTriggered();
    }
    else if (event.type === 'webchat/close') {
      this.handleCloseEvent();
    }
  }

  private handleOpenEvent() {
    if (this.isAutoFocusInputDisabled()) {
      this.focusInputOnLargeDevice(); // We focus the input field since auto focus should only be disabled for mobile.
    }

    this.disableBackgroundScrolling();
    this.changeHeaderVisibility();

    // Webchat will be readded back to the DOM so we need to add the button behavior again
    // This check also prevents double binding of said behavior.
    this.addButtonBehavior();

    this.chatSessionService.setWindowState(ChatWindowState.Open);
    // Restore the input field state
    const inputState = this.chatSessionService.getInputState() ?? InputState.Shown;
    this.chatSessionService.setInputState(inputState);
  }

  private addButtonBehavior() {
    this.addMinimizeBahaviour();
    this.addCloseBehaviour();
  }

  private addMinimizeBahaviour() {
    // You can't see the difference between minimizing and closing during the webchat/close event,
    // so the window state is set on button click events.
    // This can be used to define closing behavior (e.g. reset the persistent session).
    const minimizeButton = document.querySelector('button[data-header-minimize-button]');
    minimizeButton.addEventListener('click', () => {
      this.chatSessionService.setWindowState(ChatWindowState.Minimized);
      this.analyticsService.dispatchFrontEndAnalytics('Gesprek verbergen (minimize)');
    });
  }

  private addCloseBehaviour() {
    const closeButton = document.querySelector('button[data-header-close-button]');
    closeButton.addEventListener('click', () => {
      this.analyticsService.dispatchFrontEndAnalytics('Gesprek afsluiten (kruisje)');
      this.reinitialize();
    });
  }

  private handleCloseEvent(): void {
    if (this.chatSessionService.getWindowState() === ChatWindowState.Closed) {
      this.changeBubbleVisibility(!this.hideBubbleWhenClosed);
    }

    this.enableBackgroundScrolling();
  }

  private disableBackgroundScrolling() {
    document.body.classList.add('webchat-open');
  }

  private enableBackgroundScrolling() {
    document.body.classList.remove('webchat-open');
  }

  private isCustomEvent(event: any) {
    return event.type === 'webchat/incoming-message' &&
      event.payload?.data?.hasOwnProperty('eventId');
  }

  private isAutoFocusInputDisabled() {
    return this.options.settings.widgetSettings.disableInputAutofocus;
  }

  private focusInputOnLargeDevice() {
    if (screen.width > (this.options.deviceMinWidthToFocusInput ?? 880)) {
      const input = document.getElementsByClassName("webchat-input-message-input")[0] as HTMLElement;
      input?.focus();
    }
  }

  private handleCustomEvent(event: any) {
    const handler = this.options.eventHandlerConfigs
      ?.find(config => config.eventId === event.payload.data.eventId)?.handler;
    if (handler == null) {
      console.warn(`No eventHandler for event with eventId: ${event.payload.data.eventId}`);
      return;
    }
    handler(event.payload.data, this.webchat, this.chatSessionService);
  }

  private changeBubbleVisibility(isVisible: boolean) {
    const chatBubble = document.getElementsByClassName('webchat-toggle-button')[0] as HTMLElement;

    if (!chatBubble) return;

    if (isVisible) {
      chatBubble.style.display = "initial";
    }
    else {
      chatBubble.style.display = "none";
    }
  }

  private forwardAccessTokenIfLoginWasTriggered() {
    if (this.chatSessionService.popLoginTriggered()) {
      // Wait for 1 second before sending token just to make sure if all messages were loaded
      setTimeout(() => {
        console.debug('Forwarding user access token after login');
        forwardAccessTokenFromUserMenu(this.webchat);
      }, 1000);
    }
  }

  private afterLoad(webchat: any) {
    const detail: WebChatReadyEvent = {
      sessionId: webchat.client.socketOptions.sessionId,
      userId: webchat.client.socketOptions.userId
    };

    withGlobalFunctions();

    window.dispatchEvent(new CustomEvent<WebChatReadyEvent>('postnl:webchat:ready', { detail } ));
  }

  private onWebchatOpen(callback: Function) {
    this.onMutation(mutationRecord => (mutationRecord.addedNodes?.[0] as HTMLElement)?.classList?.contains("webchat-root")
      || document.getElementById('webchatWindow'), callback, true);
  }

  private onWebchatRootAdded(callback: Function) {
    this.onMutation(mutationRecord => (mutationRecord.addedNodes?.[0] as HTMLElement)?.classList?.contains("webchat-root"), callback, true);
  }

  private onMutation(mutationsFilter: Function, callback: Function, once: boolean) {
    const observerCallback = mutationRecords => {
      const targetMutation = mutationRecords.filter(mutationsFilter)[0];
        if (targetMutation) {
          callback();
          if (once) {
            observer.disconnect();
          }
        }
      };
    const observer = new MutationObserver(observerCallback);
    observer.observe(document.body, { childList: true, subtree: true });
  }
}
